import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Modal,
  Button,
  Alert,
  Col,
  Spinner,
  Row,
  Card,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import moment from "moment";
import {
  createFlightHomePageOffer,
  updateFlightHomePageOffer,
  fetchFlightHomePageOfferById,
  fetchGalleryImageUpdate,
} from "../../services/userservice";

const AddOffersManagementPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    offer_type: 1,
    image_url: "",
    applicable_type: 1,
    title: "",
    sub_title: "",
    description: "",
    offer_detail: "",
    avail_offer: "",
    term_condition: "",
    sequence: "",
    valid_from: moment().format("YYYY-MM-DD"),
    valid_to: moment().format("YYYY-MM-DD"),
    status: "active",
  });

  const [previewImage, setPreviewImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [previewMainImage, setPreviewMainImage] = useState("");
  const [baseUrl, setBaseUrl] = useState(null);

  const offerTypeMap = {
    1: "homepage_offer",
    2: "special_offer",
    3: "slider_offer",
  };

  const getUploadType = () => {
    console.log("offerTypeMap", formData.offer_type);
    return offerTypeMap[formData.offer_type] || "banner";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        [name]:
          name === "offer_type" || name === "applicable_type" ||  name === "sequence"
            ? Number(value)
            : value,
      };

      if (name === "valid_from" && value > prev.valid_to) {
        updatedFormData.valid_to = value;
      }

      return updatedFormData;
    });
  };

  const handleEditorChange = (name, data) => {
    setFormData((prev) => ({ ...prev, [name]: data }));
  };

  const handleMainBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadType = getUploadType();
    const formDataData = new FormData();
    formDataData.append("profile_pic", file);
    formDataData.append("type", uploadType);

    try {
      setUploading(true);
      const response = await fetchGalleryImageUpdate(formDataData);
      const uploadedFile = response?.data?.files?.[0];

      if (uploadedFile) {
        setFormData((prev) => ({
          ...prev,
          image_url: uploadedFile.image_url,
        }));
        setPreviewMainImage(uploadedFile.base_url + uploadedFile.image_url);

        console.log(
          `Image uploaded successfully for ${uploadType}:`,
          uploadedFile.image_url
        );
      }
    } catch (error) {
      console.error("Upload error:", error);
      setErrorMsg(`Image upload failed for ${uploadType}!`);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);

    try {
      if (id) {
        await updateFlightHomePageOffer(id, formData);
      } else {
        await createFlightHomePageOffer(formData);
      }
      setShowModal(true);
      setTimeout(() => {
        navigate("/offers-management-list");
      }, 2000);
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      const fetchOffer = async () => {
        try {
          setLoading(true);
          const res = await fetchFlightHomePageOfferById(id);
          setFormData({
            offer_type: res?.offer_type || 1,
            image_url: res?.image_url || "",
            applicable_type: res?.applicable_type || 1,
            sequence: res?.sequence || 1,
            title: res?.title || "",
            sub_title: res?.sub_title || "",
            description: res?.description || "",
            offer_detail: res?.offer_detail || "",
            avail_offer: res?.avail_offer || "",
            term_condition: res?.term_condition || "",
            valid_from: moment(res?.valid_from).format("YYYY-MM-DD"),
            valid_to: moment(res?.valid_to).format("YYYY-MM-DD"),
            status: res?.status,
          });
          setBaseUrl(res?.banner_url || "");
          setPreviewMainImage(res?.base_url + res?.image_url);
          setPreviewImage(res?.base_url + res?.image_url);
        } catch (err) {
          setErrorMsg("Error loading offer");
        } finally {
          setLoading(false);
        }
      };
      fetchOffer();
    }
  }, [id]);

  const getCurrentOfferTypeName = () => {
    const typeNames = {
      1: "Home Offer",
      2: "Special Offer",
      3: "Slider Offer",
    };
    return typeNames[formData.offer_type] || "Offer";
  };

  const getMinValidToDate = () => {
    return formData.valid_from;
  };

  const getBannerUploadInfo = () => {
    const info = {
      1: {
        // Home Offer
        title: "Home Offer Banner",
        description:
          "This banner will be displayed on the homepage main offer section",
        dimensions: "692x225 pixels",
        format: "JPG, PNG, WebP, AVIF",
        position: "Homepage featured offers section",
        maxSize: "3MB",
      },
      2: {
        // Special Offer
        title: "Special Offer Banner",
        description:
          "This banner will be used in special offers detailed pages",
        dimensions: "688x220 pixels",
        format: "JPG, PNG, WebP, AVIF",
        position: "Special offers listing and detail pages",
        maxSize: "3MB",
      },
      3: {
        // Slider Offer
        title: "Slider Banner",
        description: "This banner will be used in homepage slider/carousel",
        dimensions: "335x170 pixels",
        format: "JPG, PNG, WebP, AVIF",
        position: "Homepage slider/carousel section",
        maxSize: "3MB",
      },
    };
    return info[formData.offer_type] || info[1];
  };

  const UploadInfoTooltip = (props) => (
    <Tooltip id="upload-info-tooltip" {...props}>
      <div className="text-left">
        <strong>Upload Specifications:</strong>
        <div className="small">
          <div>
            📐 <strong>Dimensions:</strong> {getBannerUploadInfo().dimensions}
          </div>
          <div>
            📁 <strong>Formats:</strong> {getBannerUploadInfo().format}
          </div>
          <div>
            💾 <strong>Max Size:</strong> {getBannerUploadInfo().maxSize}
          </div>
          <div>
            📍 <strong>Position:</strong> {getBannerUploadInfo().position}
          </div>
        </div>
      </div>
    </Tooltip>
  );

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 text-dark">
                {id
                  ? `Edit ${getCurrentOfferTypeName()}`
                  : `Add ${getCurrentOfferTypeName()}`}
              </h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/flight-home-page-offer">Flight Offers</Link>
                </li>
                <li className="breadcrumb-item active">
                  {id ? "Edit" : "Add"}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          {errorMsg && (
            <Alert
              variant="danger"
              onClose={() => setErrorMsg(null)}
              dismissible
            >
              {errorMsg}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <div className="card">
              <div className="card-body">
                <div className="row">
                  <Row>
                    <Col lg={4}>
                      <div className="form-group">
                        <label>Title *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          placeholder="Enter offer title"
                          required
                        />
                      </div>
                    </Col>

                    <Col lg={4}>
                      <div className="form-group">
                        <label>Offer Type *</label>
                        <select
                          className="form-control"
                          name="offer_type"
                          value={formData.offer_type}
                          onChange={handleInputChange}
                        >
                          <option value={1}>Home Offer</option>
                          <option value={2}>Special Offer</option>
                          <option value={3}>Slider Offer</option>
                        </select>
                        <small className="form-text text-muted">
                          Upload type: <strong>{getUploadType()}</strong>
                        </small>
                      </div>
                    </Col>

                    <Col lg={4}>
                      <div className="form-group">
                        <label>Applicable Type *</label>
                        <select
                          className="form-control"
                          name="applicable_type"
                          value={formData.applicable_type}
                          onChange={handleInputChange}
                        >
                          <option value={1}>Flight</option>
                          <option value={2}>Hotel</option>
                          <option value={3}>Holiday</option>
                        </select>
                      </div>
                    </Col>

                    <Col lg={3}>
                      <div className="form-group">
                        <label>Sequence *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="sequence"
                          value={formData.sequence}
                          onChange={handleInputChange}
                          placeholder="Enter sequence "
                          required
                        />
                      </div>
                    </Col>

                    <Col lg={3}>
                      <div className="form-group">
                        <label>Valid From *</label>
                        <input
                          type="date"
                          className="form-control"
                          name="valid_from"
                          value={formData.valid_from}
                          onChange={handleInputChange}
                         // min={moment().format("YYYY-MM-DD")}
                          required
                        />
                        <small className="form-text text-muted">
                          {formData.valid_from === moment().format("YYYY-MM-DD")
                            ? "Default: Today's date"
                            : `Selected: ${moment(formData.valid_from).format(
                                "DD MMM YYYY"
                              )}`}
                        </small>
                      </div>
                    </Col>

                    <Col lg={3}>
                      <div className="form-group">
                        <label>Valid To *</label>
                        <input
                          type="date"
                          className="form-control"
                          name="valid_to"
                          value={formData.valid_to}
                          onChange={handleInputChange}
                         // min={getMinValidToDate()} // Can't select dates before valid_from
                          required
                        />
                        <small className="form-text text-muted">
                          {formData.valid_to === formData.valid_from
                            ? `Same as Valid From date`
                            : `Selected: ${moment(formData.valid_to).format(
                                "DD MMM YYYY"
                              )}`}
                        </small>
                      </div>
                    </Col>

                    <Col lg={3}>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          className="form-control"
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </Col>

                    {/* <Col lg={12}>
                                            <div className="form-group">
                                                <label htmlFor="bannerImage">
                                                    {getCurrentOfferTypeName()} Image *
                                                    <small className="text-muted ml-2">
                                                        (Type: {getUploadType()})
                                                    </small>
                                                </label>
                                                <div className="custom-file">
                                                    <input
                                                        type="file"
                                                        className="custom-file-input"
                                                        id="bannerImage"
                                                        onChange={handleMainBannerUpload}
                                                        accept="image/*"
                                                    />
                                                    <label className="custom-file-label" htmlFor="bannerImage">
                                                        {uploading ? "Uploading..." : `Choose ${getCurrentOfferTypeName().toLowerCase()} image`}
                                                    </label>
                                                </div>
                                                {uploading && (
                                                    <div className="mt-2">
                                                        <Spinner animation="border" size="sm" className="mr-2" />
                                                        Uploading {getCurrentOfferTypeName().toLowerCase()} image...
                                                    </div>
                                                )}
                                                <small className="form-text text-muted">
                                                    Image will be uploaded as <strong>{getUploadType()}</strong> type
                                                </small>
                                            </div>
                                        </Col> */}
                    <Col lg={12}>
                      <div className="form-group">
                        <label htmlFor="bannerImage">
                          Upload Banner Image *
                          <OverlayTrigger
                            placement="top"
                            overlay={UploadInfoTooltip}
                          >
                            <small
                              className="text-muted ml-2"
                              style={{ cursor: "help" }}
                            >
                              <i className="fas fa-info-circle"></i> View
                              specifications
                            </small>
                          </OverlayTrigger>
                        </label>

                        {uploading && (
                          <div className="mt-2">
                            <Spinner
                              animation="border"
                              size="sm"
                              className="mr-2"
                            />
                            <small>
                              Uploading banner image as{" "}
                              <strong>{getUploadType()}</strong>...
                            </small>
                          </div>
                        )}

                        <div className="custom-file">
                          <input
                            type="file"
                            className="custom-file-input"
                            id="bannerImage"
                            onChange={handleMainBannerUpload}
                            accept="image/jpeg, image/jpg, image/png, image/webp, image/avif"
                          />
                          <label
                            className="custom-file-label"
                            htmlFor="bannerImage"
                          >
                            {uploading
                              ? "Uploading..."
                              : `Choose ${getCurrentOfferTypeName().toLowerCase()} banner`}
                          </label>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {(previewMainImage || formData.image_url) && (
                    <div className="form-group">
                      <label>Image Preview</label>
                      <div className="border rounded p-3 text-center bg-light">
                        <img
                          src={previewMainImage || baseUrl + formData.image_url}
                          alt="Offer Preview"
                          className="img-fluid rounded"
                          style={{
                            maxHeight: "300px",
                            maxWidth: "100%",
                            objectFit: "contain",
                          }}
                        />
                        <div className="mt-2">
                          <small className="text-muted">
                            Current image for {getCurrentOfferTypeName()}
                          </small>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.offer_type === 2 && (
                    <Row>
                      <Col lg={12}>
                        <div className="form-group">
                          <label>Sub Title</label>
                          <CKEditor
                            editor={ClassicEditor}
                            data={formData.sub_title}
                            onChange={(event, editor) =>
                              handleEditorChange("sub_title", editor.getData())
                            }
                          />
                        </div>
                      </Col>

                      <Col lg={12}>
                        <div className="form-group">
                          <label>Description</label>
                          <CKEditor
                            editor={ClassicEditor}
                            data={formData.description}
                            onChange={(event, editor) =>
                              handleEditorChange(
                                "description",
                                editor.getData()
                              )
                            }
                          />
                        </div>
                      </Col>

                      <Col lg={12}>
                        <div className="form-group">
                          <label>Offer Detail</label>
                          <CKEditor
                            editor={ClassicEditor}
                            data={formData.offer_detail}
                            onChange={(event, editor) =>
                              handleEditorChange(
                                "offer_detail",
                                editor.getData()
                              )
                            }
                          />
                        </div>
                      </Col>

                      <Col lg={12}>
                        <div className="form-group">
                          <label>Avail Offer</label>
                          <CKEditor
                            editor={ClassicEditor}
                            data={formData.avail_offer}
                            onChange={(event, editor) =>
                              handleEditorChange(
                                "avail_offer",
                                editor.getData()
                              )
                            }
                          />
                        </div>
                      </Col>

                      <Col lg={12}>
                        <div className="form-group">
                          <label>Terms & Conditions</label>
                          <CKEditor
                            editor={ClassicEditor}
                            data={formData.term_condition}
                            onChange={(event, editor) =>
                              handleEditorChange(
                                "term_condition",
                                editor.getData()
                              )
                            }
                          />
                        </div>
                      </Col>
                    </Row>
                  )}

                  <Col lg={12}>
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      disabled={loading || uploading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="mr-2"
                          />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save mr-2"></i>
                          Save {getCurrentOfferTypeName()}
                        </>
                      )}
                    </button>

                    <Link
                      to="/offers-management-list"
                      className="btn btn-default btn-lg mx-4"
                    >
                      <i className="fas fa-arrow-left mr-2"></i>
                      Back to List
                    </Link>
                  </Col>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Success Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="w-100 text-center">
            <i className="fas fa-check-circle text-success mr-2"></i>
            Success
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-3">
            <i
              className="fas fa-check-circle text-success"
              style={{ fontSize: "4rem" }}
            ></i>
          </div>
          <h5>{getCurrentOfferTypeName()} saved successfully!</h5>
          <p className="text-muted">Redirecting to offers list...</p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center">
          <Button variant="success" onClick={() => setShowModal(false)}>
            <i className="fas fa-check mr-2"></i>
            Continue
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AddOffersManagementPage;
